import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './AdminPanel.css';

const AdminPanel = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastFetched, setLastFetched] = useState(null);
  const navigate = useNavigate();

  // Handle logout
  const handleLogout = async () => {
    try {
      // Call backend to invalidate token
      await api.post('/api/admin/logout');
    } catch (err) {
      // Continue with logout even if backend call fails
      console.error('Logout error:', err);
    } finally {
      // Clear session storage
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminAuthenticated');
      // Force page reload to reset authentication state
      window.location.href = '/admin';
    }
  };

  // Fetch leads from backend
  const fetchLeads = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.get('/api/leads');
      setLeads(response.data.leads || []);
      setLastFetched(new Date().toLocaleString());
    } catch (err) {
      // If unauthorized (401), token is invalid/expired - logout user
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      
      setError(
        err.response?.data?.error || 
        'Failed to fetch leads. Please try again.'
      );
      setLeads([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <div className="container">
        <div className="admin-header">
          <div className="admin-header-content">
            <div>
              <h1>Garnet Group Estate - Admin Panel</h1>
              <p>Manage and view all submitted leads</p>
            </div>
            <button 
              onClick={handleLogout}
              className="btn btn-secondary logout-btn"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="admin-actions">
          <button 
            onClick={fetchLeads} 
            className="btn btn-primary fetch-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Fetching Leads...' : 'Fetch Leads'}
          </button>
          
          {lastFetched && (
            <p className="last-fetched">
              Last fetched: {lastFetched}
            </p>
          )}
        </div>

        {error && (
          <div className="alert alert-error">
            ✗ {error}
          </div>
        )}

        {leads.length > 0 && (
          <div className="leads-container">
            <div className="leads-header">
              <h2>Leads ({leads.length})</h2>
            </div>
            
            <div className="leads-table-container">
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Property Interest</th>
                    <th>Submitted Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead._id}>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.phoneNumber}</td>
                      <td>
                        <span className="property-badge">
                          {lead.propertyInterest}
                        </span>
                      </td>
                      <td>
                        {new Date(lead.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {leads.length === 0 && !isLoading && !error && lastFetched && (
          <div className="no-leads">
            <p>No leads found.</p>
          </div>
        )}

        {leads.length === 0 && !isLoading && !error && !lastFetched && (
          <div className="no-leads">
            <p>Click "Fetch Leads" to retrieve all submitted leads.</p>
          </div>
        )}

        <div className="admin-footer">
          <a href="/" className="back-link">← Back to Landing Page</a>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;

